import { useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { RegisterUser } from "../../Services/Operations/ProductAPI";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

const GOLD = "#e5932e";
const TAN = "#bba58b";
const WHITE = "#ffffff";

const tools = ["📐", "🪚", "🔨", "🪛", "🏠", "🪟", "🚪", "🪜"];

function FloatingIcon({ icon, x, y, delay, size }) {
  return (
    <motion.div
      style={{
        position: "absolute",
        left: `${x}%`,
        top: `${y}%`,
        fontSize: size,
        opacity: 0,
        pointerEvents: "none",
        zIndex: 0,
        filter: "blur(0.5px)",
      }}
      animate={{
        opacity: [0, 0.18, 0],
        y: [0, -30, -60],
        rotate: [0, 15, -10],
      }}
      transition={{
        duration: 6,
        delay,
        repeat: Infinity,
        repeatDelay: Math.random() * 4 + 2,
        ease: "easeInOut",
      }}
    >
      {icon}
    </motion.div>
  );
}


const Register = () => {
    const navigate=useNavigate()
  const [form, setForm] = useState({
     full_name: "",
     phone: "",
     email: "",
     birth_date: "",
     password: "",
     password_confirmation: "",
   });
   const [showPass, setShowPass] = useState(false);
   const [showConfirm, setShowConfirm] = useState(false);
   const [focused, setFocused] = useState(null);
   const [submitted, setSubmitted] = useState(false);
   const [loading, setLoading] = useState(false);
   const [errors, setErrors] = useState({});
 
   const [floaters] = useState(() =>
     Array.from({ length: 12 }, (_, i) => ({
       icon: tools[i % tools.length],
       x: Math.random() * 90 + 5,
       y: Math.random() * 80 + 10,
       delay: i * 0.7,
       size: `${Math.random() * 14 + 14}px`,
     }))
   );
 
   const handleChange = (field) => (e) => {
     setForm((prev) => ({ ...prev, [field]: e.target.value }));
     setErrors((prev) => ({ ...prev, [field]: "" }));
   };
 
   const validate = () => {
     const newErrors = {};
     if (!form.full_name.trim()) newErrors.full_name = "Full name is required";
     if (!form.phone.trim()) newErrors.phone = "Phone is required";
     else if (!/^\d{10}$/.test(form.phone)) newErrors.phone = "Enter valid 10-digit phone";
     if (!form.email.trim()) newErrors.email = "Email is required";
     else if (!/\S+@\S+\.\S+/.test(form.email)) newErrors.email = "Enter valid email";
     if (!form.birth_date) newErrors.birth_date = "Birth date is required";
     if (!form.password) newErrors.password = "Password is required";
     else if (form.password.length < 8) newErrors.password = "Min 8 characters";
     if (!form.password_confirmation) newErrors.password_confirmation = "Please confirm password";
     else if (form.password !== form.password_confirmation) newErrors.password_confirmation = "Passwords do not match";
     return newErrors;
   };
 
   const handleSubmit = async() => {
     const newErrors = validate();
     if (Object.keys(newErrors).length > 0) {
       setErrors(newErrors);
       return;
     }
     setLoading(true);
     try {
        const res=await RegisterUser(form)
        console.log(res);
        if(res.status===201){
            toast.success('Member registered successfully')
           setLoading(false);
       setSubmitted(true); 
       navigate("/login")
        }
        
     } catch (error) {
        console.log(error);
        toast.error("Failed to register")
        setLoading(false);
     }
    
   };
 
   const inputStyle = (field) => ({
     width: "100%",
     padding: "13px 16px 13px 44px",
     background: focused === field ? "rgba(229,147,46,0.08)" : "rgba(187,165,139,0.07)",
     border: `1px solid ${errors[field] ? "rgba(220,80,80,0.6)" : focused === field ? "rgba(229,147,46,0.5)" : "rgba(187,165,139,0.2)"}`,
     borderRadius: 12,
     color: WHITE,
     fontSize: 14,
     fontFamily: "'Josefin Sans', sans-serif",
     fontWeight: 300,
     outline: "none",
     transition: "all 0.3s ease",
     boxShadow: focused === field
       ? `0 0 0 3px rgba(229,147,46,0.12), 0 4px 16px rgba(229,147,46,0.08)`
       : errors[field]
       ? `0 0 0 3px rgba(220,80,80,0.08)`
       : "none",
   });
 
   const labelStyle = (field) => ({
     display: "block",
     fontFamily: "'Josefin Sans', sans-serif",
     fontSize: 10,
     fontWeight: 600,
     letterSpacing: "0.18em",
     textTransform: "uppercase",
     color: errors[field] ? "rgba(220,120,120,0.9)" : focused === field ? GOLD : TAN,
     marginBottom: 7,
     transition: "color 0.3s",
   });
 
   const fields = [
     {
       key: "full_name", label: "Full Name", type: "text",
       placeholder: "John Renovator", icon: "👤", col: "full",
     },
     {
       key: "email", label: "Email Address", type: "email",
       placeholder: "you@example.com", icon: "✉️", col: "full",
     },
     {
       key: "phone", label: "Phone Number", type: "tel",
       placeholder: "9330790727", icon: "📱", col: "half",
     },
     {
       key: "birth_date", label: "Date of Birth", type: "date",
       placeholder: "", icon: "🎂", col: "half",
     },
   ];
 
   return (
     <div
       style={{
         minHeight: "100vh",
         width: "100%",
         fontFamily: "'Cormorant Garamond', Georgia, serif",
         position: "relative",
         overflow: "hidden",
         display: "flex",
         alignItems: "center",
         justifyContent: "center",
         background: `
           radial-gradient(ellipse at 70% 30%, rgba(229,147,46,0.18) 0%, transparent 60%),
           radial-gradient(ellipse at 20% 80%, rgba(187,165,139,0.22) 0%, transparent 55%),
           linear-gradient(135deg, #1a1208 0%, #2d1f0e 35%, #1c1407 70%, #0e0b05 100%)
         `,
         padding: "24px 0",
       }}
     >
       <style>{`
         @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;1,300;1,400&family=Josefin+Sans:wght@300;400;600&display=swap');
 
         * { box-sizing: border-box; margin: 0; padding: 0; }
 
         input:-webkit-autofill {
           -webkit-box-shadow: 0 0 0 100px rgba(187,165,139,0.08) inset !important;
           -webkit-text-fill-color: #fff !important;
         }
         input[type="date"]::-webkit-calendar-picker-indicator {
           filter: invert(0.6) sepia(1) saturate(2) hue-rotate(5deg);
           opacity: 0.6;
           cursor: pointer;
         }
 
         .field-input::placeholder { color: rgba(187,165,139,0.45); }
 
         .register-btn:hover {
           background: linear-gradient(135deg, #f0a84a 0%, #e5932e 50%, #c97d22 100%) !important;
           transform: translateY(-2px) !important;
           box-shadow: 0 12px 40px rgba(229,147,46,0.5), 0 0 0 1px rgba(229,147,46,0.3) !important;
         }
         .register-btn:active { transform: translateY(0px) !important; }
         .register-btn:disabled { opacity: 0.7; cursor: not-allowed; }
 
         .err-msg {
           font-family: 'Josefin Sans', sans-serif;
           font-size: 10px;
           color: rgba(220,120,120,0.9);
           letter-spacing: 0.06em;
           margin-top: 5px;
           padding-left: 4px;
         }
 
         .two-col {
           display: grid;
           grid-template-columns: 1fr 1fr;
           gap: 14px;
         }
 
         @media (max-width: 480px) {
           .two-col { grid-template-columns: 1fr; }
         }
       `}</style>
 
       {/* Background grid */}
       <svg
         style={{ position: "absolute", inset: 0, width: "100%", height: "100%", opacity: 0.06, pointerEvents: "none" }}
         xmlns="http://www.w3.org/2000/svg"
       >
         <defs>
           <pattern id="grid" width="60" height="60" patternUnits="userSpaceOnUse">
             <path d="M 60 0 L 0 0 0 60" fill="none" stroke={TAN} strokeWidth="0.5" />
           </pattern>
         </defs>
         <rect width="100%" height="100%" fill="url(#grid)" />
       </svg>
 
       {/* Floating icons */}
       {floaters.map((f, i) => (
         <FloatingIcon key={i} {...f} />
       ))}
 
       {/* Diagonal accent */}
       <motion.div
         initial={{ scaleX: 0, opacity: 0 }}
         animate={{ scaleX: 1, opacity: 1 }}
         transition={{ duration: 1.2, ease: "easeOut", delay: 0.3 }}
         style={{
           position: "absolute",
           top: 0, right: 0,
           width: "38%", height: "100%",
           background: `linear-gradient(160deg, rgba(229,147,46,0.07) 0%, transparent 60%)`,
           borderLeft: `1px solid rgba(229,147,46,0.12)`,
           transformOrigin: "right center",
           pointerEvents: "none",
         }}
       />
 
       <AnimatePresence mode="wait">
         {!submitted ? (
           <motion.div
             key="register-form"
             initial={{ opacity: 0, y: 48, scale: 0.96 }}
             animate={{ opacity: 1, y: 0, scale: 1 }}
             exit={{ opacity: 0, y: -30, scale: 0.95 }}
             transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
             style={{
               position: "relative",
               zIndex: 10,
               width: "100%",
               maxWidth: 480,
               margin: "20px",
               background: `linear-gradient(145deg, rgba(255,255,255,0.06) 0%, rgba(187,165,139,0.06) 100%)`,
               backdropFilter: "blur(28px)",
               WebkitBackdropFilter: "blur(28px)",
               border: `1px solid rgba(187,165,139,0.22)`,
               borderRadius: 24,
               padding: "44px 44px 40px",
               boxShadow: `
                 0 40px 80px rgba(0,0,0,0.6),
                 0 0 0 1px rgba(229,147,46,0.1),
                 inset 0 1px 0 rgba(255,255,255,0.08),
                 0 8px 32px rgba(229,147,46,0.08)
               `,
             }}
           >
             {/* Top accent line */}
             <motion.div
               initial={{ scaleX: 0 }}
               animate={{ scaleX: 1 }}
               transition={{ duration: 0.9, delay: 0.5, ease: "easeOut" }}
               style={{
                 position: "absolute",
                 top: 0, left: "10%",
                 width: "80%", height: 2,
                 background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)`,
                 borderRadius: 2,
                 transformOrigin: "center",
               }}
             />
 
             {/* Logo */}
             <motion.div
               initial={{ opacity: 0, y: -16 }}
               animate={{ opacity: 1, y: 0 }}
               transition={{ duration: 0.6, delay: 0.2 }}
               style={{ textAlign: "center", marginBottom: 28 }}
             >
               <div style={{
                 width: 56, height: 56,
                 borderRadius: 16,
                 background: `linear-gradient(135deg, ${GOLD} 0%, #c97d22 100%)`,
                 display: "flex", alignItems: "center", justifyContent: "center",
                 margin: "0 auto 14px",
                 boxShadow: `0 8px 24px rgba(229,147,46,0.4), inset 0 1px 0 rgba(255,255,255,0.2)`,
                 fontSize: 24,
               }}>
                 🏛️
               </div>
               <h1 style={{
                 fontFamily: "'Cormorant Garamond', serif",
                 fontSize: 26, fontWeight: 600,
                 color: WHITE, letterSpacing: "0.04em",
                 lineHeight: 1.1, marginBottom: 5,
               }}>
                  Dhanushdhari Renovators
               </h1>
               <p style={{
                 fontFamily: "'Josefin Sans', sans-serif",
                 fontSize: 10, fontWeight: 300,
                 color: TAN, letterSpacing: "0.2em",
                 textTransform: "uppercase",
               }}>
                 Premium Renovation 
               </p>
             </motion.div>
 
             {/* Heading */}
             <motion.div
               initial={{ opacity: 0, x: -16 }}
               animate={{ opacity: 1, x: 0 }}
               transition={{ duration: 0.5, delay: 0.35 }}
               style={{ marginBottom: 24 }}
             >
               <h2 style={{
                 fontFamily: "'Cormorant Garamond', serif",
                 fontSize: 20, fontWeight: 400,
                 fontStyle: "italic",
                 color: `rgba(255,255,255,0.75)`,
                 letterSpacing: "0.02em",
               }}>
                 Create your account
               </h2>
               <p style={{
                 fontFamily: "'Josefin Sans', sans-serif",
                 fontSize: 10,
                 color: `rgba(187,165,139,0.6)`,
                 letterSpacing: "0.12em",
                 textTransform: "uppercase",
                 marginTop: 4,
               }}>
                 Join the renovation community
               </p>
             </motion.div>
 
             {/* Full Name & Email — full width */}
             {fields.filter(f => f.col === "full").map((field, idx) => (
               <motion.div
                 key={field.key}
                 initial={{ opacity: 0, y: 16 }}
                 animate={{ opacity: 1, y: 0 }}
                 transition={{ duration: 0.5, delay: 0.4 + idx * 0.08 }}
                 style={{ marginBottom: 14 }}
               >
                 <label style={labelStyle(field.key)}>{field.label}</label>
                 <div style={{ position: "relative" }}>
                   <span style={{
                     position: "absolute", left: 14, top: "50%",
                     transform: "translateY(-50%)",
                     fontSize: 15, opacity: 0.5, pointerEvents: "none",
                   }}>{field.icon}</span>
                   <input
                     className="field-input"
                     type={field.type}
                     value={form[field.key]}
                     onChange={handleChange(field.key)}
                     onFocus={() => setFocused(field.key)}
                     onBlur={() => setFocused(null)}
                     placeholder={field.placeholder}
                     style={inputStyle(field.key)}
                   />
                 </div>
                 {errors[field.key] && <p className="err-msg">⚠ {errors[field.key]}</p>}
               </motion.div>
             ))}
 
             {/* Phone & Birth Date — two columns */}
             <motion.div
               initial={{ opacity: 0, y: 16 }}
               animate={{ opacity: 1, y: 0 }}
               transition={{ duration: 0.5, delay: 0.56 }}
               className="two-col"
               style={{ marginBottom: 14 }}
             >
               {fields.filter(f => f.col === "half").map((field) => (
                 <div key={field.key}>
                   <label style={labelStyle(field.key)}>{field.label}</label>
                   <div style={{ position: "relative" }}>
                     <span style={{
                       position: "absolute", left: 14, top: "50%",
                       transform: "translateY(-50%)",
                       fontSize: 15, opacity: 0.5, pointerEvents: "none",
                       zIndex: 1,
                     }}>{field.icon}</span>
                     <input
                       className="field-input"
                       type={field.type}
                       value={form[field.key]}
                       onChange={handleChange(field.key)}
                       onFocus={() => setFocused(field.key)}
                       onBlur={() => setFocused(null)}
                       placeholder={field.placeholder}
                       style={{
                         ...inputStyle(field.key),
                         colorScheme: "dark",
                       }}
                     />
                   </div>
                   {errors[field.key] && <p className="err-msg">⚠ {errors[field.key]}</p>}
                 </div>
               ))}
             </motion.div>
 
             {/* Password */}
             <motion.div
               initial={{ opacity: 0, y: 16 }}
               animate={{ opacity: 1, y: 0 }}
               transition={{ duration: 0.5, delay: 0.62 }}
               style={{ marginBottom: 14 }}
             >
               <label style={labelStyle("password")}>Password</label>
               <div style={{ position: "relative" }}>
                 <span style={{
                   position: "absolute", left: 14, top: "50%",
                   transform: "translateY(-50%)",
                   fontSize: 15, opacity: 0.5, pointerEvents: "none",
                 }}>🔒</span>
                 <input
                   className="field-input"
                   type={showPass ? "text" : "password"}
                   value={form.password}
                   onChange={handleChange("password")}
                   onFocus={() => setFocused("password")}
                   onBlur={() => setFocused(null)}
                   placeholder="Min. 8 characters"
                   style={{ ...inputStyle("password"), paddingRight: 48 }}
                 />
                 <button
                   onClick={() => setShowPass(s => !s)}
                   style={{
                     position: "absolute", right: 14, top: "50%",
                     transform: "translateY(-50%)",
                     background: "none", border: "none",
                     cursor: "pointer", fontSize: 15,
                     opacity: 0.5, padding: 4, transition: "opacity 0.2s",
                   }}
                   onMouseEnter={e => e.target.style.opacity = 1}
                   onMouseLeave={e => e.target.style.opacity = 0.5}
                 >
                   {showPass ? "🙈" : "👁️"}
                 </button>
               </div>
               {errors.password && <p className="err-msg">⚠ {errors.password}</p>}
             </motion.div>
 
             {/* Confirm Password */}
             <motion.div
               initial={{ opacity: 0, y: 16 }}
               animate={{ opacity: 1, y: 0 }}
               transition={{ duration: 0.5, delay: 0.68 }}
               style={{ marginBottom: 26 }}
             >
               <label style={labelStyle("password_confirmation")}>Confirm Password</label>
               <div style={{ position: "relative" }}>
                 <span style={{
                   position: "absolute", left: 14, top: "50%",
                   transform: "translateY(-50%)",
                   fontSize: 15, opacity: 0.5, pointerEvents: "none",
                 }}>🔐</span>
                 <input
                   className="field-input"
                   type={showConfirm ? "text" : "password"}
                   value={form.password_confirmation}
                   onChange={handleChange("password_confirmation")}
                   onFocus={() => setFocused("password_confirmation")}
                   onBlur={() => setFocused(null)}
                   placeholder="Re-enter password"
                   style={{ ...inputStyle("password_confirmation"), paddingRight: 48 }}
                 />
                 <button
                   onClick={() => setShowConfirm(s => !s)}
                   style={{
                     position: "absolute", right: 14, top: "50%",
                     transform: "translateY(-50%)",
                     background: "none", border: "none",
                     cursor: "pointer", fontSize: 15,
                     opacity: 0.5, padding: 4, transition: "opacity 0.2s",
                   }}
                   onMouseEnter={e => e.target.style.opacity = 1}
                   onMouseLeave={e => e.target.style.opacity = 0.5}
                 >
                   {showConfirm ? "🙈" : "👁️"}
                 </button>
               </div>
               {errors.password_confirmation && <p className="err-msg">⚠ {errors.password_confirmation}</p>}
             </motion.div>
 
             {/* Submit */}
             <motion.div
               initial={{ opacity: 0, y: 12 }}
               animate={{ opacity: 1, y: 0 }}
               transition={{ duration: 0.5, delay: 0.74 }}
             >
               <button
                 className="register-btn"
                 onClick={handleSubmit}
                 disabled={loading}
                 style={{
                   width: "100%",
                   padding: "15px",
                   background: `linear-gradient(135deg, #f0a84a 0%, ${GOLD} 50%, #c97d22 100%)`,
                   border: "none",
                   borderRadius: 12,
                   color: "#1a0f02",
                   fontSize: 13,
                   fontFamily: "'Josefin Sans', sans-serif",
                   fontWeight: 600,
                   letterSpacing: "0.18em",
                   textTransform: "uppercase",
                   cursor: "pointer",
                   transition: "all 0.3s ease",
                   boxShadow: `0 8px 24px rgba(229,147,46,0.35), inset 0 1px 0 rgba(255,255,255,0.25)`,
                   display: "flex",
                   alignItems: "center",
                   justifyContent: "center",
                   gap: 10,
                 }}
               >
                 {loading ? (
                   <motion.span
                     animate={{ rotate: 360 }}
                     transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                     style={{ display: "inline-block", fontSize: 16 }}
                   >
                     ⚙️
                   </motion.span>
                 ) : (
                   <>
                     <span>Create Account</span>
                     <span style={{ fontSize: 14 }}>→</span>
                   </>
                 )}
               </button>
             </motion.div>
 
             {/* Login link */}
             <motion.p
               initial={{ opacity: 0 }}
               animate={{ opacity: 1 }}
               transition={{ delay: 0.82 }}
               style={{
                 textAlign: "center",
                 marginTop: 20,
                 fontFamily: "'Josefin Sans', sans-serif",
                 fontSize: 12,
                 color: "rgba(187,165,139,0.5)",
                 letterSpacing: "0.05em",
               }}
             >
               Already have an account?{" "}
               <a href="/login" style={{
                 color: GOLD, textDecoration: "none",
                 fontWeight: 600, transition: "opacity 0.2s",
               }}
                 onMouseEnter={e => e.target.style.opacity = 0.75}
                 onMouseLeave={e => e.target.style.opacity = 1}
               >
                 Sign In
               </a>
             </motion.p>
           </motion.div>
         ) : (
           /* Success */
           <motion.div
             key="success"
             initial={{ opacity: 0, scale: 0.85 }}
             animate={{ opacity: 1, scale: 1 }}
             transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
             style={{
               zIndex: 10,
               textAlign: "center",
               padding: 52,
               background: "rgba(255,255,255,0.05)",
               backdropFilter: "blur(28px)",
               border: `1px solid rgba(229,147,46,0.3)`,
               borderRadius: 24,
               boxShadow: `0 40px 80px rgba(0,0,0,0.5), 0 0 0 1px rgba(229,147,46,0.15)`,
               maxWidth: 380,
               margin: "20px",
             }}
           >
             <motion.div
               animate={{ scale: [1, 1.2, 1], rotate: [0, 12, -6, 0] }}
               transition={{ duration: 0.7, delay: 0.2 }}
               style={{ fontSize: 60, marginBottom: 20 }}
             >
               🏛️
             </motion.div>
             <motion.div
               initial={{ opacity: 0, y: 10 }}
               animate={{ opacity: 1, y: 0 }}
               transition={{ delay: 0.5 }}
             >
               <h2 style={{
                 fontFamily: "'Cormorant Garamond', serif",
                 fontSize: 28, fontWeight: 600,
                 color: WHITE, marginBottom: 10,
               }}>
                 Welcome Aboard!
               </h2>
               <p style={{
                 fontFamily: "'Josefin Sans', sans-serif",
                 fontSize: 11, color: TAN,
                 letterSpacing: "0.12em",
                 textTransform: "uppercase",
                 marginBottom: 24,
               }}>
                 Your account has been created
               </p>
               <div style={{
                 width: 48, height: 2,
                 background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)`,
                 margin: "0 auto 24px",
               }} />
               <p style={{
                 fontFamily: "'Josefin Sans', sans-serif",
                 fontSize: 12,
                 color: "rgba(187,165,139,0.6)",
                 letterSpacing: "0.06em",
               }}>
                 Redirecting to your workspace…
               </p>
             </motion.div>
           </motion.div>
         )}
       </AnimatePresence>
 
       {/* Bottom brand mark */}
       <motion.div
         initial={{ opacity: 0 }}
         animate={{ opacity: 0.35 }}
         transition={{ delay: 1.2 }}
         style={{
           position: "absolute", bottom: 20, left: "50%",
           transform: "translateX(-50%)",
           fontFamily: "'Josefin Sans', sans-serif",
           fontSize: 10, color: TAN,
           letterSpacing: "0.2em",
           textTransform: "uppercase",
           whiteSpace: "nowrap",
           zIndex: 10,
         }}
       >
         © 2026 RenovaSpace · All Rights Reserved
       </motion.div>
     </div>
   );
}

export default Register
